/// <reference path="../../app.definition.ts" />

import angular from "angular";
import * as _ from "lodash";
import { Subject } from "@common/model/subject";
import { User } from "@common/model/user";
import { JavaApiAdapterService } from "@common/apiadapter/javaapi.service";
import { CacheService } from "@common/service/cache.service";
import { IBroadcastService } from "@common/service/broadcast.service";

export interface ISubjectService {
    getCurrentSubject(): Subject;
    setCurrentSubject(subject: Subject): void;
    registerSubjectChangeCallback(key: string, callback: Function): void;
    getTeamMembers(subject?: Subject, success?: Function, error?: Function, always?: Function)
    reset(): void;
    updateSubject(subjectId: number, input: any): void;
    getSubjectsPgWise(pg): any;
    getSubjectById(subjectId: number): any;
    getSubjectListByIds(ids);
    checkUserMembership(id): ng.IPromise<any>;
}

export class SubjectService implements ISubjectService {
    subject: Subject;
    callbacks: any = {};
    callbackNames: Array<String> = new Array<String>();
    pageSize = 10;

    static $inject = ["CacheService", "ApiAdapterService", "$q", "BroadcastService"];
    constructor(
        private $_cache: CacheService,
        private $_api: JavaApiAdapterService,
        private $q: ng.IQService,
        private BroadcastService: IBroadcastService
    ) { }

    unregisterSubjectChangeCallback(key: string): void {
        if (!!this.callbacks[key]) {
            delete this.callbacks[key];
        }
    }

    registerSubjectChangeCallback(key: string, callback: Function): void {
        //console.log('SubjectService: registering callback with key: ', key);
        if (!this.callbacks[key]) {
            this.callbacks[key] = callback;
        }
    }

    setCurrentSubject(subject:  Subject): void {
        !!subject && (this.subject = subject) || (this.subject = null);

        angular.forEach(this.callbacks, (callback, key) => {
            //console.log('SubjectService: Calling subjectChangeCallback with key: ', key);
            callback(this.subject);
        });
    }

    getCurrentSubject(): Subject {
        return (this.subject = this.$_cache.getCurrentSubject() || null);
    }

    getTeamMembers(subject?: Subject, success?: Function, error?: Function, always?: Function): void {
        !!subject || (subject = this.getCurrentSubject());

        //console.log('SubjectService: Loading teammembers for subject: ', subject);

        this.$_api.getTeamMembers(subject.subjectId).then(
            (s: User[]) => {

                // sort Team Members by team owner
                var owner = s.filter(function (member) {
                    return subject.ownerId == member.id
                });

                //remove team owner from list
                if (owner !== null && owner !== undefined && owner.length > 0) {
                    s = s.filter(function (member) {
                        return member.id !== owner[0].id;
                    });
                    //add team owner on top of the element 0th position
                    s.unshift(owner[0]);
                }
                //console.log('SubjectService: Loaded teammembers: ', s);
                subject.teamMembers = s;

                if (!!success) {
                    success(subject);
                }
                if (!!always) {
                    always(subject);
                }
            },
            (e) => {
                //console.error('SubjectService: Failed to load teammembers');

                if (!!error) {
                    error(subject);
                }
                if (!!always) {
                    always(subject);
                }
            });

    }

    updateSubject(subjectId: number, input: any): void {
        var process = (teams) => {
            var indexOfSubject = teams.findIndex(sub => sub.subjectId == subjectId);
            if (indexOfSubject >= 0) {
                let subject = new Subject().deserialize(teams[indexOfSubject]);
                subject.update(input);
                teams.splice(indexOfSubject, 1, subject);
                return teams;
            }
            return null;
        }

        let updatedTeams = process(this.$_cache.getSortedSubjects());
        if (!!updatedTeams) {
            this.$_cache.setSortedSubjects(updatedTeams);
        }
        updatedTeams = process(this.$_cache.getSubjects());
        if (!!updatedTeams) {
            this.$_cache.setSubjects(updatedTeams);
        }
        let sub = this.$_cache.getCurrentSubject();
        if (!!sub && sub.subjectId == subjectId) {
            sub = new Subject().deserialize(sub);
            sub.update(input);
            this.$_cache.setCurrentSubject(sub);
        }

        this.BroadcastService.broadcast('refresh-teams-drpdn');
        this.BroadcastService.broadcast('cr-updated');
    }

    reset(): void {
        this.subject = null;
        this.callbacks = {};
        this.callbackNames = new Array<String>();
    }

    getSubjectById = (subjectId) => {
        var deferred = this.$q.defer();
        let cachedSub = this.$_cache.getSortedSubjects();
        let subs = this.$_cache.getSubjects();
        let allSub = cachedSub.concat(subs);
        let subj = allSub.find(sub => sub.subjectId == subjectId);
        if (!subj) {
            this.$_api.getSubject(subjectId, this.$_cache.getPassword()).then(s => {
                if (!!s) {
                    subs.push(s);
                    this.$_cache.setSubjects(subs);
                }
                deferred.resolve(s);
            });
        } else {
            deferred.resolve(subj);
        }
        return deferred.promise;
    }

    getSubjectListByIds = (ids) => {
        var deferred = this.$q.defer();
        let result = {}, nonCachedIds = [];
        let cachedSub = this.$_cache.getSortedSubjects();
        let subs = this.$_cache.getSubjects();
        let allSub = cachedSub.concat(subs);
        for (let subId of ids) {
            let subj = allSub.find(sub => sub.subjectId == subId);
            if (subj) {
                result[subId] = subj;
            } else {
                nonCachedIds.push(subId);
            }
        }

        if (nonCachedIds.length > 0) {
            this.$_api.getSubjectsById(nonCachedIds).then(s => {
                if (!!s) {
                    subs = subs.concat(s);
                    this.$_cache.setSubjects(subs);
                }
                result = _.reduce(s, (ac, obj) => {
                    ac[obj['subjectId']] = obj;
                    return ac;
                }, result);
                deferred.resolve(result);
            });
        } else {
            deferred.resolve(result);
        }
        return deferred.promise;
    }

    getSubjectsPgWise = (pg) => {
        var deferred = this.$q.defer();
        let cachedSub = this.$_cache.getSortedSubjects();
        let totalSubs = this.$_cache.getTotalSubjects();
        let cachedSubLen = (cachedSub || []).length;
        let start = pg * this.pageSize, end = start + this.pageSize;
        if (cachedSubLen >= end || (totalSubs > 0 && cachedSubLen == totalSubs)) {
            deferred.resolve(cachedSub.slice(start, end));
        } else {
            this.$_api.getSubjectsV3(pg, this.pageSize, null).then((data) => {
                data['subjects'].forEach((s) => {
                    if (!cachedSub.find(sub => sub.subjectId == s.subjectId)) {
                        cachedSub.push(s);
                    }
                });
                this.$_cache.setSortedSubjects(cachedSub);
                this.$_cache.setTotalSubjects(data['totalElements']);
                this.$_cache.set('userMembership', data['totalElements'], true);
                deferred.resolve(data['subjects']);
            },
                (e) => {
                    console.log('Error while fetching subject list');
                    deferred.reject(e);
                });
        }
        return deferred.promise;
    }
    checkUserMembership = (userId): ng.IPromise<any> => {
        var deferred = this.$q.defer();
        let cacheUserMembership = this.$_cache.get('userMembership', true)
        if (typeof cacheUserMembership !== "undefined") {
            deferred.resolve(cacheUserMembership);
        } else {
            this.$_api.checkMembership(userId).then((data) => {
                let count = !!data.permissionSubjectCount ? (data.teamMemberSubjectCount + data.permissionSubjectCount) : data.teamMemberSubjectCount
                this.$_cache.set('userMembership', count, true);
                deferred.resolve(count);
            })
        }
        return deferred.promise;
    }
}

angular.module('eCareVault.common.services')
    .service('SubjectService', SubjectService);
